import { EditGridView, SellableOption } from '../../../types/edit-grid';
import { EditGridModalProps } from '../../components/EditGridModal';
import { ComplimentaryColor, ComplimentaryColorArray, Product } from '../../../types/types';
import { updateGridSize } from './product-grid-utils';
import { JSX } from 'preact';
import { DropdownItemProps, DropdownLabelProps } from 'ts/components/Dropdown';

export const getEditGridModalProps = (view: EditGridView): EditGridModalProps => {
    const { state, update } = view;

    if (!state.editModal) {
        return;
    }

    const row = state.editModal.row;
    const col = state.editModal.col;
    const product = state.grid.products[state.editModal.row][state.editModal.col];

    // Build options from all sellable types
    const productOptions: SellableOption[] = state.products.products.flatMap((p) =>
        p.variants.map((v): SellableOption => ({
            id: v.id,
            name: `${p.name}` + (v.name ? ` (${v.name})` : ''),
            active: p.active && !v.deleted,
            type: 'product',
            variantId: v.id,
            section: state.lang.modal.sections.products,
        }))
    );

    const serialTicketOptions: SellableOption[] = state.products.serialTickets.map((t): SellableOption => ({
        id: t.id,
        name: t.name,
        active: !t.deleted,
        type: 'serial_ticket',
        serialTicketTypeId: t.id,
        section: state.lang.modal.sections.serialTickets,
    }));

    const giftCardOptions: SellableOption[] = state.products.giftCards.map((g): SellableOption => ({
        id: g.id,
        name: g.name,
        active: !g.deleted,
        type: 'gift_card',
        giftCardTypeId: g.id,
        section: state.lang.modal.sections.giftCards,
    }));

    const membershipCardOptions: SellableOption[] = state.products.membershipCards.map((m): SellableOption => ({
        id: m.id,
        name: m.name,
        active: !m.deleted,
        type: 'membership_card',
        cardTypeId: m.id,
        isRenewal: m.isRenewal,
        section: state.lang.modal.sections.membershipCards,
    }));

    const allOptions: SellableOption[] = [
        ...productOptions,
        ...serialTicketOptions,
        ...giftCardOptions,
        ...membershipCardOptions,
    ];

    const activeOptions: SellableOption[] = allOptions.filter((p) => p.active);

    // Find current selection based on the grid item data
    const currentSelection = allOptions.find(
        (x) =>
            (x.variantId && x.variantId === state.editModal.data.variantId) ||
            (x.serialTicketTypeId && x.serialTicketTypeId === state.editModal.data.serialTicketTypeId) ||
            (x.giftCardTypeId && x.giftCardTypeId === state.editModal.data.giftCardTypeId) ||
            (x.cardTypeId &&
                x.cardTypeId === state.editModal.data.cardTypeId &&
                x.isRenewal === state.editModal.data.isRenewal)
    );

    const productModal: EditGridModalProps = {
        show: true,
        title: 'Muuda ruutu',
        titleField: {
            value: state.editModal.data.title,
            onChange: (value) => {
                state.editModal.data.title = value;
                update();
            },
        },
        productField: {
            open: state.editModal.dropdownOpen,
            name: 'product',
            value: currentSelection,
            searchValue: state.editModal.searchValue ?? currentSelection?.name ?? '',
            options: activeOptions,
            onOutsideClick: () => {
                state.editModal.dropdownOpen = false;
                update();
            },
            onDropdownClick: () => {
                state.editModal.dropdownOpen = !state.editModal.dropdownOpen;
                state.editModal.searchValue = '';
                update();
            },
            onSelect(value: SellableOption): void {
                // Clear all ID fields first
                state.editModal.data.variantId = undefined;
                state.editModal.data.serialTicketTypeId = undefined;
                state.editModal.data.giftCardTypeId = undefined;
                state.editModal.data.cardTypeId = undefined;
                state.editModal.data.isRenewal = undefined;

                // Set the appropriate ID based on type
                if (value.type === 'product') {
                    state.editModal.data.variantId = value.variantId;
                } else if (value.type === 'serial_ticket') {
                    state.editModal.data.serialTicketTypeId = value.serialTicketTypeId;
                } else if (value.type === 'gift_card') {
                    state.editModal.data.giftCardTypeId = value.giftCardTypeId;
                } else if (value.type === 'membership_card') {
                    state.editModal.data.cardTypeId = value.cardTypeId;
                    state.editModal.data.isRenewal = value.isRenewal;
                }

                state.editModal.dropdownOpen = false;
                update();
            },
            searchParameter: (item) => item?.name,
            onSearchInput: (value) => {
                state.editModal.searchValue = value;
                update();
            },
        },
        colorField: {
            name: 'color',
            defaultValue: 'pink',
            value: state.editModal.data.color,
            options: ComplimentaryColorArray.map((color) => ({
                value: color,
                label: color,
            })),
            onChange: (value) => {
                state.editModal.data.color = value as ComplimentaryColor;
                update();
            },
        },
        toggleModal: () => {
            state.editModal = null;
            update();
        },
        isSaveDisabled: !currentSelection,
        closeLabel: state.lang.modal.close,
        saveLabel: state.lang.modal.save,
        colorLabel: state.lang.modal.color,
        titleLabel: state.lang.modal.title,
        productLabel: state.lang.modal.product,
        selectItemLabel: state.lang.modal.selectItem,
    };

    // Check if any sellable item is selected
    const hasSelection =
        state.editModal.data.variantId ||
        state.editModal.data.serialTicketTypeId ||
        state.editModal.data.giftCardTypeId ||
        state.editModal.data.cardTypeId;

    if (hasSelection) {
        productModal.onSave = () => {
            state.anyChanges = true;

            if (state.editModal.data.title == '') {
                state.editModal.data.title = currentSelection?.name ?? '';
            }

            state.grid.products[row][col] = state.editModal.data;
            state.editModal = null;
            updateGridSize(view);
            update();
        };
    }

    if (product) {
        productModal.onRemove = () => {
            state.anyChanges = true;
            state.grid.products[row][col] = null;
            state.editModal = null;
            updateGridSize(view);
            update();
        };
    }

    return productModal;
};
